import React, { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2, ShieldCheck, LogIn, KeyRound, Smartphone, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { Logo } from "../components/Logo";

// Login flow with 2FA + enrolment:
//   step = "credentials"  → email + password
//   step = "challenge"    → 6-digit TOTP or 10-char recovery code
//   step = "enrol-scan"   → show QR + recovery codes, wait for user to confirm
//   step = "enrol-verify" → user types 6-digit code to finish enrolment
export const LoginPage = () => {
  const {
    user, loading, setupRequired,
    login, setup,
    submitTwoFactorChallenge, startTwoFactorSetup, verifyTwoFactorSetup,
  } = useAuth();
  const loc = useLocation();
  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [challengeToken, setChallengeToken] = useState("");
  const [challengeMethod, setChallengeMethod] = useState("totp"); // "email" | "totp"
  const [challengeEmail, setChallengeEmail] = useState("");
  const [emailSentOk, setEmailSentOk] = useState(true);
  const [emailErrorHint, setEmailErrorHint] = useState("");
  const [setupToken, setSetupToken] = useState("");
  const [code, setCode] = useState("");
  const [enrolBundle, setEnrolBundle] = useState(null); // {qr_data_url, otpauth, secret_base32, recovery_codes}
  const [savedRecovery, setSavedRecovery] = useState(false);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0f1416] text-white"><Loader2 className="animate-spin" /></div>;
  if (user) {
    const nextParam = new URLSearchParams(loc.search).get("next");
    const roleHome = { manager: "/admin", admin: "/admin", office_admin: "/admin", fitter: "/team", client: "/client" };
    const fallback = roleHome[user.role] || "/";
    const to = nextParam || loc.state?.from || fallback;
    return <Navigate to={to} replace />;
  }

  const isSetup = setupRequired;

  const submitCredentials = async (e) => {
    e.preventDefault();
    if (isSetup && password !== confirmPw) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      if (isSetup) {
        await setup({ email, password, name: name.trim() || "Administrator" });
        toast.success("Admin account created — welcome!");
        return;
      }
      const r = await login(email, password);
      if (r.kind === "signed-in") {
        toast.success("Signed in");
      } else if (r.kind === "two-factor-required") {
        setChallengeToken(r.challenge_token);
        setChallengeMethod(r.method || "totp");
        setChallengeEmail(r.user_email || email);
        setEmailSentOk(r.email_sent !== false);
        setEmailErrorHint(r.email_error || "");
        setStep("challenge");
      } else if (r.kind === "two-factor-setup-required") {
        setSetupToken(r.setup_token);
        toast.message("Two-factor setup required for your role", { description: "One-time enrolment — takes 20 seconds." });
        setStep("enrol-start");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-in failed");
    } finally { setBusy(false); }
  };

  const submitChallenge = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitTwoFactorChallenge(challengeToken, code.trim());
      toast.success("Signed in");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
      setCode("");
    } finally { setBusy(false); }
  };

  const beginEnrolment = async () => {
    setBusy(true);
    try {
      const bundle = await startTwoFactorSetup(setupToken);
      setEnrolBundle(bundle);
      setStep("enrol-scan");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start 2FA setup");
    } finally { setBusy(false); }
  };

  const submitEnrolVerify = async (e) => {
    e.preventDefault();
    if (!savedRecovery) return toast.error("Please confirm you've saved your recovery codes");
    setBusy(true);
    try {
      await verifyTwoFactorSetup(setupToken, code.trim());
      toast.success("Two-factor authentication is now enabled");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
      setCode("");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f1416] via-[#20282c] to-[#0f1416] flex items-center justify-center p-4 sm:p-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><Logo variant="group" size="lg" dark /></div>
        <div className="bg-white rounded-md p-5 sm:p-6 shadow-2xl">
          {step === "credentials" && (
            <CredentialsForm
              isSetup={isSetup} email={email} setEmail={setEmail}
              password={password} setPassword={setPassword}
              confirmPw={confirmPw} setConfirmPw={setConfirmPw}
              name={name} setName={setName} submit={submitCredentials} busy={busy}
              expired={new URLSearchParams(loc.search).get("expired")}
            />
          )}
          {step === "challenge" && (
            <ChallengeForm
              method={challengeMethod}
              email={challengeEmail}
              emailSentOk={emailSentOk}
              emailErrorHint={emailErrorHint}
              code={code} setCode={setCode} submit={submitChallenge}
              busy={busy} onCancel={() => { setStep("credentials"); setCode(""); }}
              onResend={async () => {
                // Re-run login to get a fresh challenge + a new email.
                const r = await login(email, password);
                if (r.kind === "two-factor-required") {
                  setChallengeToken(r.challenge_token);
                  setEmailSentOk(r.email_sent !== false);
                  setEmailErrorHint(r.email_error || "");
                  toast.success(r.email_sent !== false ? "New code sent — check your email." : "Could not send email — check with your admin.");
                }
              }}
            />
          )}
          {step === "enrol-start" && (
            <EnrolStart onBegin={beginEnrolment} busy={busy} />
          )}
          {step === "enrol-scan" && enrolBundle && (
            <EnrolScan
              bundle={enrolBundle} onContinue={() => setStep("enrol-verify")}
              savedRecovery={savedRecovery} setSavedRecovery={setSavedRecovery}
            />
          )}
          {step === "enrol-verify" && (
            <EnrolVerify
              code={code} setCode={setCode} submit={submitEnrolVerify}
              busy={busy} savedRecovery={savedRecovery} onBack={() => setStep("enrol-scan")}
            />
          )}
        </div>
        <p className="text-center text-xs text-white/50 mt-4">Diesel &amp; Dust Group · Secure sign-in</p>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-display uppercase tracking-widest text-[#3f4b52] block mb-1">{label}</span>
    {children}
  </label>
);

const CredentialsForm = ({ isSetup, email, setEmail, password, setPassword, confirmPw, setConfirmPw, name, setName, submit, busy, expired }) => (
  <>
    <div className="text-center mb-6">
      <div className="inline-flex items-center gap-2 bg-[#f28a16]/10 text-[#f28a16] px-3 py-1 rounded-sm text-xs font-display uppercase tracking-widest">
        {isSetup ? <ShieldCheck size={12} /> : <LogIn size={12} />}
        {isSetup ? "First-run setup" : "Secure sign in"}
      </div>
      <h1 className="mt-2 font-display font-black uppercase text-2xl text-[#111619]">
        {isSetup ? "Create your admin account" : "Welcome back"}
      </h1>
      {isSetup && <p className="text-xs text-[#3f4b52] mt-1">No users found. Set up the first administrator to secure this workspace.</p>}
      {expired && !isSetup && (
        <div
          data-testid="session-expired-banner"
          className="mt-3 text-xs text-[#7f1d1d] bg-[#fef2f2] border border-[#fecaca] rounded-md p-2"
        >
          Your session expired — please sign in again to continue.
        </div>
      )}
    </div>
    <form onSubmit={submit} className="space-y-3" data-testid={isSetup ? "setup-form" : "login-form"}>
      {isSetup && (
        <Field label="Your name">
          <input value={name} onChange={(e) => setName(e.target.value)} className="w-full h-11 px-3 border border-[#cfd7db] rounded-md text-sm" data-testid="setup-name" />
        </Field>
      )}
      <Field label="Email">
        <input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-11 px-3 border border-[#cfd7db] rounded-md text-sm" data-testid="auth-email" />
      </Field>
      <Field label={isSetup ? "Password (min 8 chars)" : "Password"}>
        <input type="password" required minLength={isSetup ? 8 : 1} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full h-11 px-3 border border-[#cfd7db] rounded-md text-sm" data-testid="auth-password" />
      </Field>
      {isSetup && (
        <Field label="Confirm password">
          <input type="password" required value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="w-full h-11 px-3 border border-[#cfd7db] rounded-md text-sm" data-testid="auth-password2" />
        </Field>
      )}
      <button type="submit" disabled={busy} data-testid="auth-submit"
        className="w-full bg-[#f28a16] text-white rounded-md py-3 font-display uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 className="animate-spin" size={16} /> : <KeyRound size={16} />}
        {isSetup ? "Create admin & sign in" : "Sign in"}
      </button>
    </form>
  </>
);

const ChallengeForm = ({ method, email, emailSentOk, emailErrorHint, code, setCode, submit, busy, onCancel, onResend }) => {
  const isEmail = method === "email";
  return (
    <>
      <div className="text-center mb-5">
        <div className="inline-flex items-center gap-2 bg-[#f28a16]/10 text-[#f28a16] px-3 py-1 rounded-sm text-xs font-display uppercase tracking-widest">
          {isEmail ? <span>📧 Sign-in code</span> : <><Smartphone size={12} /> Two-factor authentication</>}
        </div>
        <h1 className="mt-2 font-display font-black uppercase text-xl text-[#111619]">
          {isEmail ? "Check your email" : "Enter a verification code"}
        </h1>
        {isEmail ? (
          <p className="text-xs text-[#3f4b52] mt-1">
            We just sent a <b>6-digit code</b> to <b>{email}</b>. It expires in 5 minutes.
            {!emailSentOk && (
              <span className="block mt-1 text-[#dc2626]">⚠ Email delivery failed — please ask your admin to check the Resend settings. Details: {emailErrorHint || "unknown"}</span>
            )}
          </p>
        ) : (
          <p className="text-xs text-[#3f4b52] mt-1">
            <b>Two options:</b> the <b>6-digit code</b> currently shown in your authenticator <b>OR</b> one of the <b>10-character recovery codes</b> you saved during setup.
          </p>
        )}
      </div>
      <form onSubmit={submit} className="space-y-3" data-testid="challenge-form">
        <Field label={isEmail ? "6-digit code from your email" : "6-digit code · or · 10-char recovery code"}>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required autoFocus autoComplete="one-time-code"
            inputMode={isEmail ? "numeric" : "text"}
            maxLength={isEmail ? 6 : undefined}
            placeholder={isEmail ? "123456" : "123 456   or   ABCD12EFGH"}
            className="w-full h-12 px-3 border border-[#cfd7db] rounded-md text-lg font-mono tracking-widest text-center uppercase"
            data-testid="challenge-code"
          />
        </Field>
        <button type="submit" disabled={busy || !code.trim()} data-testid="challenge-submit"
          className="w-full bg-[#f28a16] text-white rounded-md py-3 font-display uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <KeyRound size={16} />}
          Verify &amp; sign in
        </button>
        {isEmail && onResend && (
          <button type="button" onClick={onResend} disabled={busy}
            data-testid="challenge-resend"
            className="w-full text-xs text-[#f28a16] hover:text-[#c66c0e] py-2 disabled:opacity-40">
            Didn't get the email? Send a fresh code
          </button>
        )}
        <button type="button" onClick={onCancel} className="w-full text-xs text-[#3f4b52] hover:text-[#111619] py-2">
          ← Cancel and use a different account
        </button>
      </form>
    </>
  );
};

const EnrolStart = ({ onBegin, busy }) => (
  <>
    <div className="text-center mb-5">
      <div className="inline-flex items-center gap-2 bg-[#f28a16]/10 text-[#f28a16] px-3 py-1 rounded-sm text-xs font-display uppercase tracking-widest">
        <ShieldCheck size={12} /> Two-factor setup required
      </div>
      <h1 className="mt-2 font-display font-black uppercase text-xl text-[#111619]">Secure your account</h1>
      <p className="text-xs text-[#3f4b52] mt-2">
        Your role has access to money-changing screens (invoices, payments, users) so we require two-factor authentication.
        You'll scan a QR code with an authenticator app and save 10 single-use recovery codes for lost-phone recovery.
      </p>
    </div>
    <button type="button" onClick={onBegin} disabled={busy} data-testid="enrol-begin"
      className="w-full bg-[#f28a16] text-white rounded-md py-3 font-display uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
      {busy ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />}
      Begin 2FA setup
    </button>
  </>
);

const EnrolScan = ({ bundle, onContinue, savedRecovery, setSavedRecovery }) => {
  const downloadRecovery = () => {
    const blob = new Blob(
      [`Diesel & Dust Group — 2FA recovery codes\nGenerated ${new Date().toISOString()}\n\n${bundle.recovery_codes.join("\n")}\n\nEach code is single-use. Store these somewhere safe (password manager, printed copy in a lockbox).\n`],
      { type: "text/plain" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "dd-2fa-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
    setSavedRecovery(true);
  };
  return (
    <>
      <div className="text-center mb-4">
        <h2 className="font-display font-black uppercase text-lg text-[#111619]">1. Scan this QR code</h2>
        <p className="text-xs text-[#3f4b52] mt-1">Open your authenticator app and scan. If you can't scan, tap the key below to copy the secret manually.</p>
      </div>
      <div className="flex justify-center">
        <img src={bundle.qr_data_url} alt="2FA QR code" className="h-52 w-52 rounded-md border border-[#cfd7db] bg-white p-2" data-testid="enrol-qr" />
      </div>
      <div className="mt-3 text-center">
        <button
          type="button"
          onClick={() => { navigator.clipboard?.writeText(bundle.secret_base32); toast.success("Secret copied — paste it into your authenticator"); }}
          className="text-xs font-mono tracking-widest text-[#3f4b52] hover:text-[#111619] bg-[#f5f6f6] border border-[#cfd7db] rounded px-3 py-1 inline-block"
          data-testid="enrol-secret"
        >
          {bundle.secret_base32}
        </button>
      </div>

      <div className="mt-5 border-t border-[#cfd7db] pt-4">
        <h2 className="font-display font-black uppercase text-lg text-[#111619]">2. Save your recovery codes</h2>
        <p className="text-xs text-[#3f4b52] mt-1 mb-2">Store these somewhere safe. Each one works exactly once if you lose your phone.</p>
        <div className="grid grid-cols-2 gap-1 bg-[#f5f6f6] border border-[#cfd7db] rounded-md p-3 text-sm font-mono tracking-widest" data-testid="enrol-recovery-codes">
          {bundle.recovery_codes.map((c) => <div key={c}>{c}</div>)}
        </div>
        <button
          type="button"
          onClick={downloadRecovery}
          data-testid="enrol-download-recovery"
          className="mt-2 w-full border border-[#cfd7db] text-[#3f4b52] hover:bg-[#f5f6f6] rounded-md py-2 text-xs font-display uppercase tracking-widest inline-flex items-center justify-center gap-2"
        >
          <Download size={14} /> Download recovery codes (.txt)
        </button>
        <label className="mt-3 flex items-start gap-2 text-xs text-[#3f4b52]">
          <input type="checkbox" checked={savedRecovery} onChange={(e) => setSavedRecovery(e.target.checked)} data-testid="enrol-saved-check" className="mt-0.5" />
          I have saved these 10 recovery codes somewhere safe.
        </label>
      </div>

      <button
        type="button"
        onClick={onContinue}
        disabled={!savedRecovery}
        data-testid="enrol-continue"
        className="mt-4 w-full bg-[#f28a16] text-white rounded-md py-3 font-display uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40"
      >
        Continue → verify code
      </button>
    </>
  );
};

const EnrolVerify = ({ code, setCode, submit, busy, savedRecovery, onBack }) => (
  <>
    <div className="text-center mb-5">
      <h2 className="font-display font-black uppercase text-lg text-[#111619]">3. Verify one code</h2>
      <p className="text-xs text-[#3f4b52] mt-1">Enter the 6-digit code shown in your authenticator right now to finish setup.</p>
    </div>
    <form onSubmit={submit} className="space-y-3" data-testid="enrol-verify-form">
      <Field label="6-digit code">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required autoFocus autoComplete="one-time-code"
          inputMode="numeric"
          maxLength={6}
          placeholder="123 456"
          className="w-full h-12 px-3 border border-[#cfd7db] rounded-md text-lg font-mono tracking-widest text-center"
          data-testid="enrol-verify-code"
        />
      </Field>
      <button type="submit" disabled={busy || !savedRecovery || !code.trim()} data-testid="enrol-verify-submit"
        className="w-full bg-[#f28a16] text-white rounded-md py-3 font-display uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />}
        Enable 2FA &amp; sign in
      </button>
      <button type="button" onClick={onBack} className="w-full text-xs text-[#3f4b52] hover:text-[#111619] py-2">
        ← Back to QR code
      </button>
    </form>
  </>
);

export default LoginPage;
